import {
    ApplicationRef,
    inject,
    Injectable,
    makeStateKey,
    NgZone,
    PLATFORM_ID,
    TransferState,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';

import { filter, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { Config } from '../config';
import { ContentService } from '../content';
import { WINDOW } from '@src/app/shared/models/tokens';
import { PseResult, PseSearchService } from './pse-search.service';

declare let Cookiebot: any;

const COOKIEBOT_LANG = makeStateKey<string>('cookiebot-lang');

type AnalyticsWindow = Window & {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    __gcse?: any;
};

@Injectable({
    providedIn: 'root',
})
export class ScriptLoaderService {
    private scriptsLoaded: boolean = false;
    private initialized = false;
    private googleSearchReady: Promise<void>;
    private appRef = inject(ApplicationRef);
    private ngZone = inject(NgZone);
    private config = inject(Config);
    private platformId = inject(PLATFORM_ID);
    private window = inject(WINDOW) as AnalyticsWindow;
    private document = inject(DOCUMENT);
    private contentService = inject(ContentService);
    private http = inject(HttpClient);
    private router = inject(Router);
    private transferState = inject(TransferState);
    private pseSearchService = inject(PseSearchService);

    /**
     * Load Cookiebot, which loads the tracking scripts once statistics consent is given.
     * On the server this only looks up Cookiebot's language for the browser to reuse.
     */
    init(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        if (isPlatformServer(this.platformId)) {
            const language = this.config.language;
            this.contentService
                .getComponent('language-mapper')
                .pipe(
                    map((lang: any) =>
                        lang.find((obj) => obj.iso3 === language),
                    ),
                )
                .subscribe({
                    next: (lang) => {
                        const cookieBotLang = lang ? lang.iso2 : 'EN';
                        this.transferState.set(COOKIEBOT_LANG, cookieBotLang);
                    },
                    error: (error) => {
                        this.transferState.set(COOKIEBOT_LANG, 'EN');
                    },
                });
        } else {
            this.window.dataLayer = this.window.dataLayer || [];

            const cookieBotLang = this.transferState.get(COOKIEBOT_LANG, null);
            this.appRef.isStable
                .pipe(
                    filter((stable) => stable),
                    take(1),
                )
                .subscribe(() => {
                    this.ngZone.runOutsideAngular(() => {
                        if (cookieBotLang) {
                            this.loadCookieBot(cookieBotLang);
                        }
                    });
                });
        }
    }

    /**
     * Load Google's search library on demand instead of with the other scripts.
     */
    loadGoogleSearch(): Promise<void> {
        if (!this.googleSearchReady) {
            this.googleSearchReady = new Promise((resolve, reject) => {
                this.window.__gcse = {
                    parsetags: 'explicit',
                    initializationCallback: resolve,
                    searchCallbacks: {
                        web: {
                            ready: (
                                gname: string,
                                query: string,
                                promos: unknown,
                                results: PseResult[],
                                resultsDiv: unknown,
                            ) => this.pseSearchService.pushResults(results),
                            rendered: (
                                gname: string,
                                query: string,
                                promoElts: unknown,
                                resultElts: unknown,
                            ) => this.pseSearchService.pushRendered(),
                        },
                    },
                };

                const cseScript = this.document.createElement('script');
                cseScript.async = true;
                cseScript.src =
                    'https://cse.google.com/cse.js?cx=007569901527703748273:sm08oqewkmm';
                cseScript.onerror = () => {
                    // Clear the failed load so the next search can try again
                    this.googleSearchReady = null;
                    reject(new Error('Failed to load Google search'));
                };

                this.ngZone.runOutsideAngular(() => {
                    this.document.head.appendChild(cseScript);
                });
            });

            // Preloads ignore the result, so a failed load shouldn't surface as
            // an unhandled rejection. Callers that need it still get the error.
            this.googleSearchReady.catch(() => {});
        }

        return this.googleSearchReady;
    }

    private loadCookieBot(lang: string): void {
        const cookieStyles$: Observable<string> = this.http.get(
            'assets/css/cookiebot.css',
            { responseType: 'text' },
        );
        const scripts = this.document.scripts[0],
            head = this.document.head,
            cookieBotScript = this.document.createElement('script'),
            cookieCustomStyle = this.document.createElement('style');
        cookieBotScript.id = 'Cookiebot';
        cookieBotScript.async = !0;
        cookieBotScript.src = 'https://consent.cookiebot.com/uc.js';
        cookieBotScript.setAttribute(
            'data-cbid',
            '1d3a0b98-8f87-43a1-a681-075da67224b2',
        );
        cookieBotScript.setAttribute('data-culture', lang);
        cookieStyles$.subscribe((styles) => {
            cookieCustomStyle.appendChild(this.document.createTextNode(styles));
            scripts.parentNode.insertBefore(cookieCustomStyle, scripts);
            head.appendChild(cookieBotScript);
        });
        this.window.addEventListener(
            'CookiebotOnAccept',
            this.onCookieBotAccept.bind(this),
            false,
        );
    }

    private onCookieBotAccept(): void {
        if (Cookiebot.consent.statistics) {
            this.loadScripts();
        }
    }

    private loadScripts(): void {
        if (isPlatformBrowser(this.platformId)) {
            const environmentConfig = this.config.environment;
            const defaultSiteConfig =
                this.config.sites[environmentConfig['default_sbu']];
            this.loadGA4Scripts(defaultSiteConfig);
            this.loadGTMScripts(defaultSiteConfig);
            this.loadClarityHeatMap(defaultSiteConfig);

            this.scriptsLoaded = true;
            this.trackPageViews().subscribe();
        }
    }

    private loadGA4Scripts(config: any): void {
        if (config.ga4) {
            const d = this.document.createElement('script');
            d.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4}`;
            d.setAttribute('async', 'async');
            this.document.head.appendChild(d);

            this.window.gtag =
                this.window.gtag ||
                function (...args: any) {
                    this.window.dataLayer.push(arguments);
                };
            this.window.gtag('js', new Date());
            this.window.gtag('config', config.ga4, {
                send_page_view: false,
            });
        }
    }

    private loadGTMScripts(config: any): void {
        if (config.gtm) {
            (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js',
                });
                var f = d.getElementsByTagName(s)[0],
                    j: any = d.createElement(s),
                    dl = l != 'dataLayer' ? '&l=' + l : '';

                j.async = true;
                j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                d.body.appendChild(j);
            })(this.window, this.document, 'script', 'dataLayer', config.gtm);
        }
    }

    private trackPageViews(): Observable<RouterEvent> {
        return this.router.events.pipe(
            filter(() => this.scriptsLoaded === true),
            filter((event: NavigationEnd) => event instanceof NavigationEnd),
            tap((event: NavigationEnd) => {
                this._trackSinglePageView(event);
            }),
        );
    }

    private loadClarityHeatMap(config: any): void {
        if (config.clar) {
            (function (c, l, a, r, i, t, y) {
                c[a] =
                    c[a] ||
                    function () {
                        (c[a].q = c[a].q || []).push(arguments);
                    };
                t = l.createElement(r);
                t.async = 1;
                t.src = 'https://www.clarity.ms/tag/' + i;
                y = l.getElementsByTagName(r)[0];
                y.parentNode.insertBefore(t, y);
            })(this.window, this.document, 'clarity', 'script', config.clar);
        }
    }

    private _trackSinglePageView(event?: NavigationEnd): void {
        let pageUrl = event
            ? event.urlAfterRedirects
            : this.window.location.pathname + this.window.location.search;

        if (isPlatformBrowser(this.platformId)) {
            if (this.config.environment.ga4) {
                let pageTitle = this.window.document.title;
                this.window.gtag('config', this.config.environment.ga4, {
                    page_path: pageUrl,
                    page_title: pageTitle,
                });
            }
        }
    }
}
